import { useContext, useState } from "react";
import Select, { components } from "react-select";

import DatePicker from "react-date-picker";
import appStyles from "../../../App.module.css";
import appConstants, { addDays, formatDate, totalDaysInCurrentYear } from "../../../app-constants";
import { AUBusinessTravelContext } from "../../AUBusinessTravel";
import { colourStyles, noOfEmployee } from "../../config/bt-constants";
import { IAUBusinessTravelContext, ISelectOption } from "../../config/interfaces";
import Popup from "../Popup";
import SearchIcon from "../icons/SearchIcon";
import LoadingModal from "../modal/LoadingModal";
import EditCurrencyAndCompany from "./EditCurrencyAndCompany";

const YourQuote = () => {
  const { quoteData, version } = useContext(AUBusinessTravelContext) as IAUBusinessTravelContext;
  const [isDateEditable, setIsDateEditable] = useState<boolean>(false);
  const [isNumberOfTripsEditable, setIsNumberOfTripsEditable] = useState<boolean>(false);
  const [isNumberOfEmployeeEditable, setIsNumberOfEmployeeEditable] = useState<boolean>(false);

  return (
    <div>
      <h3 className={appStyles.btTitle}>Your quote</h3>

      <div className={`${appStyles.btQuoteHeaderDetail}`}>
        <div>
          <p className={appStyles.btSubTitle}>Policy term</p>
          <div className={`${appStyles.tripSummaryValue} ${appStyles.btTripSummaryValue}`}>
            {formatDate(new Date(quoteData.start_date))} - {formatDate(new Date(quoteData.end_date))}
          </div>
        </div>
        <span className={appStyles.btQuoteLink} onClick={() => setIsDateEditable(true)}>
          Edit
        </span>
      </div>

      {isDateEditable && (
        <Popup
          onClose={() => {
            setIsDateEditable(false);
          }}
          styleDatePicker={true}
          title="Policy Term"
          showTitle={false}
          children={<EditPolicyTerm setIsDateEditable={setIsDateEditable} />}
        />
      )}

      <div className={`${appStyles.btQuoteHeaderDetail}`}>
        <div>
          <p className={appStyles.btSubTitle}>Number of trips</p>
          <div className={`${appStyles.tripSummaryValue} ${appStyles.btTripSummaryValue}`}>
            {quoteData.noOfTrips} trips
          </div>
        </div>
        <span className={appStyles.btQuoteLink} onClick={() => setIsNumberOfTripsEditable(true)}>
          Edit
        </span>
      </div>
      {isNumberOfTripsEditable && (
        <Popup
          onClose={() => {
            setIsNumberOfTripsEditable(false);
          }}
          showTitle={false}
          title="Number of Trips"
          children={<EditNumberTrips setIsNumberOfTripsEditable={setIsNumberOfTripsEditable} />}
        />
      )}

      <div className={`${appStyles.btQuoteHeaderDetail}`}>
        <div>
          <p className={appStyles.btSubTitle}>Company size</p>
          <div className={`${appStyles.tripSummaryValue} ${appStyles.btTripSummaryValue}`}>
            {quoteData.noOfEmployee} employees
          </div>
        </div>
        <span className={appStyles.btQuoteLink} onClick={() => setIsNumberOfEmployeeEditable(true)}>
          Edit
        </span>
      </div>
      {isNumberOfEmployeeEditable && (
        <Popup
          onClose={() => {
            setIsNumberOfEmployeeEditable(false);
          }}
          title="Company size"
          showTitle={false}
          children={
            <EditCurrencyAndCompany
              itemsArray={noOfEmployee}
              closeModel={setIsNumberOfEmployeeEditable}
              type={"noOfEmployee"}
            />
          }
        />
      )}

      <hr className={appStyles.btHrStyle} />
    </div>
  );
};

export default YourQuote;

type EditPolicyTermProps = {
  setIsDateEditable: React.Dispatch<React.SetStateAction<boolean>>;
};

const EditPolicyTerm = (Props: EditPolicyTermProps) => {
  const { setIsDateEditable } = Props;
  const { quoteData, setQuoteData, setQuoteResult, setLoader, loader, createQuote } = useContext(
    AUBusinessTravelContext
  ) as IAUBusinessTravelContext;
  const [datePickerInitialValue, setDatePickerInitialValue] = useState(quoteData.start_date);
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className={`${appStyles.formRow}`}>
      <div className={appStyles.formBlock}>
        <div className={appStyles.formField}>
          <label className={` ${appStyles.btSubTitle}`}>Start date</label>
          <DatePicker
            calendarClassName={`${appStyles.bfpDatePickerCalendar} ${appStyles.btDatePickerCalendar}`}
            className={`${appStyles.bfpDatePicker} ${appStyles.btDatePicker} ${appStyles.bfpDatePickerCalendarStartDate} ${appStyles.btDatePickerCalendarStartDate}`}
            dayPlaceholder="dd"
            monthPlaceholder="mm"
            yearPlaceholder="yyyy"
            format={appConstants.datePickerFormat}
            value={datePickerInitialValue}
            maxDate={addDays(new Date(), 55)}
            id="start_date"
            onClick={() => setIsOpen(true)}
            onCalendarClose={() => setIsOpen(false)}
            isOpen={isOpen}
            onChange={(value: any) => {
              setDatePickerInitialValue(value);
            }}
            minDate={new Date()}
          />

          <div className={`${appStyles.btQuoteBtnWrap}`}>
            <button className={`${appStyles.btQuoteBtnOutline}`} onClick={() => setIsDateEditable(false)}>
              Cancel
            </button>
            <button
              className={`${appStyles.btQuoteBtn}`}
              onClick={async () => {
                try {
                  setLoader("date");
                  let _quoteData = {
                    ...quoteData,
                    start_date: datePickerInitialValue,
                    end_date: addDays(datePickerInitialValue, totalDaysInCurrentYear(datePickerInitialValue)),
                  };
                  setIsDateEditable(false);

                  const createQuoteResponse = await createQuote(_quoteData);
                  setQuoteResult(createQuoteResponse);
                  setQuoteData(_quoteData);
                } catch (e: any) {
                  console.log(e.message);
                } finally {
                  setLoader(null);
                }
              }}
            >
              Save
              {loader && (
                <Popup
                  onClose={() => {}}
                  title=""
                  children={<LoadingModal />}
                  closeButton={false}
                  LoadingModal={true}
                />
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

type EditNumberTripsProps = {
  setIsNumberOfTripsEditable: React.Dispatch<React.SetStateAction<boolean>>;
};

const EditNumberTrips = (Props: EditNumberTripsProps) => {
  const { setIsNumberOfTripsEditable } = Props;
  const { quoteData, loader, setLoader, createQuote, setQuoteResult, setQuoteData } = useContext(
    AUBusinessTravelContext
  ) as IAUBusinessTravelContext;

  // Initialize with current number
  const [initialValue, setInitialValue] = useState<number | string>(quoteData.noOfTrips || "");

  // Validation helper
  const isInvalid = initialValue === "" || Number(initialValue) < 1 || Number(initialValue) > 250;

  return (
    <div className={appStyles.formRow}>
      <div className={appStyles.formBlock}>
        <label className={` ${appStyles.btSubTitle}`}>Number of covered trips</label>

        <div className={`${appStyles.formField} ${isInvalid ? appStyles.hasError : ""}`}>
          <input
            type="number"
            className={`${appStyles.btInput} ${appStyles.bfpInput}`}
            value={initialValue}
            placeholder="Enter trips (1-250)"
            onChange={(e) => {
              const val = e.target.value === "" ? "" : Number(e.target.value);
              setInitialValue(val);
            }}
            style={{ width: '100%', height: '44px', padding: '0 12px' }}
          />
          {isInvalid && initialValue !== "" && (
            <div className={appStyles.formError}>Please enter a number between 1 and 250</div>
          )}
        </div>

        <p className={`${appStyles.btNoteWrap}`} style={{ marginTop: '20px' }}>
          This number should reflect international trips only. Domestic trips are unlimited. Each traveller counts as one trip (e.g., 3 employees on one international trip = 3 trips).
        </p>

        <div className={`${appStyles.btQuoteBtnWrap}`}>
          <button
            className={`${appStyles.btQuoteBtnOutline}`}
            onClick={() => setIsNumberOfTripsEditable(false)}
          >
            Cancel
          </button>
          <button
            className={`${appStyles.btQuoteBtn}`}
            // 1. Disable the button if the value is invalid
            disabled={isInvalid || loader === "trip"}
            onClick={async () => {
              // 2. Extra safety check before proceeding
              if (isInvalid) return;

              try {
                setLoader("trip");
                const _quoteData = {
                  ...quoteData,
                  noOfTrips: Number(initialValue),
                };

                const createQuoteResponse = await createQuote(_quoteData);
                setQuoteResult(createQuoteResponse);
                setQuoteData(_quoteData);
                setIsNumberOfTripsEditable(false);
              } catch (error) {
                console.log(error);
              } finally {
                setLoader(null);
              }
            }}
            type="button"
          >
            Save
            {loader === "trip" && (
              <Popup onClose={() => { }} title="" children={<LoadingModal />} closeButton={false} LoadingModal={true} />
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
